'use client';

import { Carousel } from '@packages/shared/src/ui/Carousel/Carousel/Carousel';
import { defineMessages, useIntl } from 'react-intl';
import type { NoMessageValues, MessageValue } from 'react-intl';
import type { ParagraphType, RecognizerComponentProps } from '../../../interfaces';
import { classes } from '../../utils/classes';
import { ComponentRecognizer } from '../ComponentRecognizer/ComponentRecognizer';
import type { CarouselType } from './types';

const messages = defineMessages<{
  readonly ariaLabelPrev: NoMessageValues;
  readonly ariaLabelNext: NoMessageValues;
  readonly buttonAutoplayPause: NoMessageValues;
  readonly buttonAutoplayStart: NoMessageValues;
  readonly slideAriaLabel: { readonly i: MessageValue; readonly n: MessageValue };
  readonly galleryAriaLabel: NoMessageValues;
}>({
  ariaLabelPrev: {
    id: 'navigation.carousel.prev.aria-label',
    defaultMessage: 'Zurück',
  },
  ariaLabelNext: {
    id: 'navigation.carousel.next.aria-label',
    defaultMessage: 'Vor',
  },
  buttonAutoplayPause: {
    id: 'navigation.carousel.pause.aria-label',
    defaultMessage: 'Automatische Slideshow anhalten',
  },
  buttonAutoplayStart: {
    id: 'navigation.carousel.start.aria-label',
    defaultMessage: 'Automatische diashow starten',
  },
  slideAriaLabel: {
    id: 'navigation.carousel.slide.aria-label',
    defaultMessage: 'Bild {i} von {n}',
  },
  galleryAriaLabel: {
    id: 'navigation.carousel.gallery.aria-label',
    defaultMessage: 'Bildergalerie',
  },
});

/**
 * Represents the Carousel component used in CMS.
 * This component displays a sequence of items (paragraphs) with various configuration options such as autoplay, loop, and slide visibility.
 *
 * @param {CarouselType} data - The data object containing the carousel configuration and content.
 * @returns A Carousel component with customized settings and content.
 */
export const CmsCarousel = ({
  data: {
    field_autoplay: autoplay = false,
    field_infinite: infinite = false,
    field_speed: duration,
    field_margin: margin = '0 0 2rem 0',
    field_padding: padding = '0',
    field_slides_to_show: slidesToShow = 1,
    paragraph,
  },
  className,
}: RecognizerComponentProps<CarouselType>) => {
  const { formatMessage } = useIntl();

  return (
    <div style={{ margin, padding }} className={classes('[& button]:absolute relative', className)}>
      <Carousel
        aria={{
          buttonPrev: formatMessage(messages.ariaLabelPrev),
          buttonNext: formatMessage(messages.ariaLabelNext),
          buttonAutoplayPause: formatMessage(messages.buttonAutoplayPause),
          buttonAutoplayStart: formatMessage(messages.buttonAutoplayStart),
          slideAriaLabel: (i, n) => formatMessage(messages.slideAriaLabel, { i: i + 1, n }),
          galleryAriaLabel: formatMessage(messages.galleryAriaLabel),
        }}
        arrowsInsideBoundary
        autoplay={autoplay}
        loop={infinite}
        visibleSlides={slidesToShow}
        {...(duration && { emblaOptions: { duration } })}
      >
        {paragraph?.map((item: ParagraphType) => (
          <ComponentRecognizer key={item.id} item={item} />
        ))}
      </Carousel>
    </div>
  );
};
